import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Button, Card, Field, IconBtn, Input, Spinner } from "../../design-system";
import { Icon, SupHeader, SupSection, SupSelect, SupKpi, SupEmpty, CategoryIcon, AssetStatusBadge } from "./atoms";
import { ASTATUS } from "./data";
import { useSoporteNav } from "./nav";
import { getSupportAssets, getSupportAssetTypes, getEmployees, createSupportAsset } from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const empName = (e) => [e?.first_name, e?.last_name].filter(Boolean).join(" ").trim() || "Sin nombre";
const TODAY = new Date();

function SoporteInventario() {
  document.title = "Inventario · Soporte IT | Kronaly";
  const nav = useSoporteNav();

  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const [q, setQ] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");

  const load = () => {
    setLoading(true);
    return getSupportAssets()
      .then((r) => { setAssets(unwrap(r) || []); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getSupportAssetTypes().then((r) => setTypes(unwrap(r) || [])).catch(() => setTypes([]));
  }, []);

  const rows = useMemo(() => assets.filter((a) => {
    if (fType && a.asset_type_id !== fType) return false;
    if (fStatus && a.status !== fStatus) return false;
    if (q.trim()) {
      const blob = `${a.code} ${a.brand || ""} ${a.model || ""} ${a.serial || ""}`.toLowerCase();
      if (!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [assets, fType, fStatus, q]);

  const disponibles = assets.filter((a) => a.status === "disponible").length;
  const reparacion = assets.filter((a) => a.status === "reparacion").length;
  const pdte = assets.filter((a) => a.status === "pdte_entrega" || a.status === "pdte_devolucion").length;

  return (
    <div className="kr-page">
      <SupHeader
        eyebrow="Soporte IT · Contexto de inventario"
        title="Inventario de equipos"
        subtitle="Activos físicos. Alimentan el contexto de los tickets; el histórico vive en el equipo, no en el usuario."
        actions={<Button variant="primary" icon="Plus" onClick={() => setCreating(true)}>Añadir activo</Button>}
      />

      {error && <Alert tone="danger" title="No se pudo cargar el inventario">{error}</Alert>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
        <SupKpi label="Total activos" value={assets.length} icon="package" />
        <SupKpi label="Disponibles" value={disponibles} icon="checkCircle" accent="var(--kr-success-600)" />
        <SupKpi label="En reparación" value={reparacion} icon="wrench" accent="var(--kr-pending-600)" />
        <SupKpi label="Pendientes entrega/devolución" value={pdte} icon="refresh" accent="var(--kr-warning-600)" />
      </div>

      <Card padded={false}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--kr-border-subtle)", flexWrap: "wrap" }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código, marca, modelo o nº de serie…" icon="Search" style={{ flex: 1, minWidth: 240, maxWidth: 380 }} />
          <SupSelect value={fType} onChange={setFType} placeholder="Tipo" options={types.map((t) => ({ value: t.id, label: t.name }))} style={{ width: 160 }} />
          <SupSelect value={fStatus} onChange={setFStatus} placeholder="Estado" options={Object.keys(ASTATUS).map((s) => ({ value: s, label: ASTATUS[s].label }))} style={{ width: 180 }} />
        </div>

        <div data-scroll-x style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div>
          ) : (
            <AssetTable rows={rows} onOpen={(id) => nav.go("activo", { id })} />
          )}
        </div>

        {!loading && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: "1px solid var(--kr-border-subtle)", color: "var(--kr-fg-muted)", fontSize: 12 }}>
            <span><span style={{ color: "var(--kr-fg-strong)", fontWeight: 600 }}>{rows.length}</span> de {assets.length} activos</span>
          </div>
        )}
      </Card>

      {creating && <ActivoForm types={types} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function AssetTable({ rows, onOpen }) {
  const cols = "1.1fr 1.6fr 1.3fr 1fr 1.1fr 1fr";
  if (!rows.length) return <SupEmpty icon="package" title="Sin activos" hint="Añade un activo o ajusta los filtros." />;
  return (
    <div style={{ minWidth: 880 }}>
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: 12, padding: "10px 20px", background: "var(--kr-neutral-50)", borderBottom: "1px solid var(--kr-border)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, color: "var(--kr-fg-muted)" }}>
        <span>Código</span><span>Equipo</span><span>Usuario asignado</span><span>Estado</span><span>Garantía</span><span>Ubicación</span>
      </div>
      {rows.map((a, i) => <AssetRow key={a.id} a={a} cols={cols} onOpen={() => onOpen(a.id)} last={i === rows.length - 1} />)}
    </div>
  );
}

function AssetRow({ a, cols, onOpen, last }) {
  const [hover, setHover] = useState(false);
  const warrantyExpired = a.warranty_until && new Date(a.warranty_until) < TODAY;
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onClick={onOpen}
      style={{ display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center", padding: "12px 20px", borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)", background: hover ? "var(--kr-bg-subtle)" : "#fff", cursor: "pointer", fontSize: 13 }}>
      <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)", fontWeight: 600 }}>{a.code}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <CategoryIcon icon={a.type?.icon} color="neutral" size={32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{[a.brand, a.model].filter(Boolean).join(" ") || "—"}</div>
          <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{a.type?.name || "—"}</div>
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        {a.current_user ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar name={a.current_user.name} size={24} />
            <span style={{ color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.current_user.name}</span>
          </div>
        ) : <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Sin asignar</span>}
      </div>
      <div><AssetStatusBadge status={a.status} /></div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: warrantyExpired ? "var(--kr-danger-700)" : "var(--kr-fg-strong)" }}>
        {a.warranty_until || "—"}{warrantyExpired && <span style={{ fontSize: 10, marginLeft: 4 }}>caducada</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--kr-fg-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.location || "—"}</div>
    </div>
  );
}

// ── Alta de activo (panel lateral) ──────────────────────────────────
function ActivoForm({ types, onClose, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ asset_type_id: "", code: "", brand: "", model: "", serial: "", status: "disponible", current_employee_profile_id: "", location: "", purchased_on: "", warranty_until: "", cost: "", provider: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const setE = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => { getEmployees().then((r) => setEmployees(unwrap(r) || [])).catch(() => {}); }, []);

  const needsUser = ["asignado", "pdte_entrega", "pdte_devolucion"].includes(f.status);
  const canSubmit = f.brand.trim() && f.model.trim() && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true); setError(null);
    try {
      await createSupportAsset({
        code: f.code.trim() || null,
        asset_type_id: f.asset_type_id || null,
        brand: f.brand.trim(),
        model: f.model.trim(),
        serial: f.serial.trim() || null,
        status: f.status,
        current_employee_profile_id: f.current_employee_profile_id || null,
        location: f.location.trim() || null,
        purchased_on: f.purchased_on || null,
        warranty_until: f.warranty_until || null,
        cost: f.cost.trim() || null,
        provider: f.provider.trim() || null,
        notes: f.notes.trim() || null,
      });
      onCreated();
    } catch (e) { setError(e?.message || String(e)); setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,19,22,0.45)", zIndex: 92 }} />
      <aside style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 600, maxWidth: "100vw", background: "#fff", borderLeft: "1px solid var(--kr-border)", boxShadow: "var(--kr-shadow-lg)", zIndex: 93, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Soporte IT · Inventario</div>
            <h2 style={{ margin: "2px 0 0", fontFamily: "var(--kr-font-display)", fontSize: 20, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{[f.brand, f.model].filter(Boolean).join(" ") || "Nuevo activo"}</h2>
          </div>
          <IconBtn icon="X" onClick={onClose} title="Cerrar" />
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 24, background: "var(--kr-neutral-50)", display: "flex", flexDirection: "column", gap: 16 }}>
          {error && <Alert tone="danger" title="No se pudo crear el activo">{error}</Alert>}
          <Card>
            <SupSection eyebrow="Identificación" title="Datos del equipo" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
              <Field label="Tipo de activo"><SupSelect value={f.asset_type_id} onChange={set("asset_type_id")} placeholder="Selecciona…" options={types.map((t) => ({ value: t.id, label: t.name }))} /></Field>
              <Field label="Código interno" help="Se genera si lo dejas vacío"><Input value={f.code} onChange={setE("code")} placeholder="KRN-0001" icon="Hash" /></Field>
              <Field label="Marca" required><Input value={f.brand} onChange={setE("brand")} placeholder="HP, Dell, Apple…" /></Field>
              <Field label="Modelo" required><Input value={f.model} onChange={setE("model")} placeholder="Latitude 5440…" /></Field>
              <Field label="Número de serie"><Input value={f.serial} onChange={setE("serial")} placeholder="Nº de serie" /></Field>
              <Field label="Ubicación"><Input value={f.location} onChange={setE("location")} placeholder="Madrid · Almacén IT" icon="MapPin" /></Field>
            </div>
          </Card>
          <Card>
            <SupSection eyebrow="Estado y asignación" title="Situación actual" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
              <Field label="Estado"><SupSelect value={f.status} onChange={set("status")} options={Object.keys(ASTATUS).map((s) => ({ value: s, label: ASTATUS[s].label }))} /></Field>
              <Field label="Usuario asignado" help={needsUser ? "Recomendado para este estado" : "Opcional"}>
                <SupSelect value={f.current_employee_profile_id} onChange={set("current_employee_profile_id")} placeholder="Sin asignar" options={employees.map((e) => ({ value: e.id, label: empName(e) }))} />
              </Field>
            </div>
          </Card>
          <Card>
            <SupSection eyebrow="Compra y garantía" title="Adquisición" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
              <Field label="Fecha de compra"><Input type="date" value={f.purchased_on} onChange={setE("purchased_on")} /></Field>
              <Field label="Garantía hasta"><Input type="date" value={f.warranty_until} onChange={setE("warranty_until")} /></Field>
              <Field label="Coste aproximado"><Input value={f.cost} onChange={setE("cost")} placeholder="749 €" /></Field>
              <Field label="Proveedor"><Input value={f.provider} onChange={setE("provider")} placeholder="PcComponentes…" /></Field>
            </div>
          </Card>
        </div>

        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--kr-border-subtle)", display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff" }}>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" icon="Check" disabled={!canSubmit} onClick={submit}>{saving ? "Guardando…" : "Añadir al inventario"}</Button>
        </div>
      </aside>
    </>
  );
}

export default SoporteInventario;
